<script setup lang="ts">
	import axios from '@/lib/axios'
	import { getRoleClasses } from '@/utils/roleColors'
	import { useDebounceFn } from '@vueuse/core'
	import { computed, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import ModalComponent from '@/components/ModalComponent.vue'
	import RolesSkeleton from '@/components/RolesSkeleton.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import SkeletonLoader from '@/components/SkeletonLoader.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'

	import { useAuth } from '@/composables/useAuth'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import { Role } from '@/modules/roles/composables/useRoles'
	import useEditUserForm from '@/modules/users/composables/useEditUserForm'
	import useUser from '@/modules/users/composables/useUser'
	import useUserRole from '@/modules/users/composables/useUserRole'
	import { canEditProfile } from '@/modules/users/helpers/canEditProfile'
	import {
		actorRank,
		canManageRolesOf,
		roleRank,
	} from '@/modules/users/helpers/roleHierarchy'
	import type { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()

	const {
		form,
		isLoadingRoles,
		roles,
		isFetchingUser,
		isLoadingUser,
		userEdit,
		hasFormChanges,
		hasFormChangesRoles,
		rolesPendingAssignment,
		addRoleToPending,
		removeRoleFromPending,
		applyPendingRoles,
		clearPendingRoles,
		refetchUser,
		// Variables de supervisor
		supervisor,
		supervisorId,
		supervisors,
		isFetchingSupervisor,
		isLoadingSupervisor,
		isFetchingSupervisors,
		setSupervisorQuery,
		refetchSupervisor,
		refetchSupervisors,
		pendingSupervisorSelection,
	} = useEditUserForm()

	const toast = useToast()

	const { updateRoleAsync } = useUserRole({
		immediate: false,
	})

	const { user: userLogged } = useAuth()

	const { hasPermissionUser } = useHasPermissionUser()

	const { updateUserAsync } = useUser({ immediate: false })

	// useUsersSearch corre con automaticSearch:false (ver useEditUserForm.ts):
	// setSupervisorQuery solo guarda el texto, nunca dispara el fetch. Sin este
	// debounce, escribir en el combobox de supervisor no mostraba resultados.
	const debouncedSearchSupervisors = useDebounceFn(() => refetchSupervisors(), 400)

	// Un Traveler solo puede editar su propio perfil: para él esta sección no
	// aplica (no gestiona supervisor/roles de nadie). El tab en EditPage.vue ya
	// se oculta para este caso (alwaysCheckPermissions), esto es un respaldo.
	const isTravelerOnly = computed(() =>
		userLogged.value?.roles?.includes('Traveler') ?? false
	)

	// Rol del objetivo: SOLO LECTURA cuando el actor no puede gestionarlo (un Travel
	// Manager sobre un Master). El backend responde 403 «No puedes modificar los
	// roles de este usuario» (RoleHierarchy::canManageRolesOf en
	// UserRoleController@store), así que ofrecer el combobox era prometer algo que
	// siempre falla — y peor: el submit mandaba el rol Y el perfil, así que salían
	// los dos toasts a la vez, el rojo del rol y el verde del perfil.
	//
	// El permiso `update-users-roles` NO sirve para esto: es global sobre Company,
	// no por objetivo, así que un TM lo tiene y no distingue a quién puede tocar.
	// La regla es: puedo editarle la info, puedo VER su rol, no puedo bajarlo.
	const canManageTargetRoles = computed(() =>
		canManageRolesOf(userLogged.value, props.user)
	)

	const rolesNoDuplicate = computed(() => {
		// Rango derivado de ASSIGNABLE_ROLES (helper), no una tabla numérica local:
		// así el test espejo de BookingV2 también vigila este filtro.
		const rank = actorRank(userLogged.value)
		const actorLevel = rank ?? Infinity

		return roles.value?.filter((role: Role) => {
			const isAlreadyAssigned = form.roles?.some(
				(assignedRole) => assignedRole.id === role.id
			)
			const isPendingAssignment = rolesPendingAssignment.some(
				(pendingRole) => pendingRole.id === role.id
			)
			if (isAlreadyAssigned || isPendingAssignment) return false

			if (isFinite(actorLevel)) {
				const roleLevel = roleRank(role.name)
				if (roleLevel === -1) return false
				// El actor asigna de su nivel para abajo: Master todo, TM de TM
				// para abajo, Supervisor de Supervisor para abajo.
				return roleLevel >= actorLevel
			}

			return true
		})
	})

	// ── Recolgado de subordinados al bajar a Traveler ─────────────────────────
	// El backend rechaza (422 en newSupervisorId) bajar a Traveler a un usuario
	// con subordinados a cargo: aquí se elige el nuevo supervisor (SOLO rol
	// Supervisor) o se dejan libres, y se reintenta.
	const showReassignModal = ref(false)
	const reassignMessage = ref('')
	const reassignSelection = ref<User | null>(null)
	const pendingRolesForReassign = ref<number[]>([])
	const reassignCandidates = ref<User[]>([])
	const loadingReassignCandidates = ref(false)

	const searchReassignCandidates = async (q = '') => {
		loadingReassignCandidates.value = true
		try {
			// Contrato del endpoint (UserSearchController): espera la key `company`
			// (no company_id) y `supervisors[]` con el rol del subordinado — regresa
			// los roles POR ENCIMA de ese rol (Supervisor/TM/Master) acotados a la
			// empresa. Abajo se estrecha a solo Supervisor; la autoridad final es
			// la regla EligibleSupervisor del backend.
			const { data } = await axios.get<User[]>('/api/users/search', {
				params: {
					q,
					supervisors: ['Traveler'],
					company: userEdit.value?.companyId,
				},
			})

			// Solo rol Supervisor como candidato: TM y Master ya ven y administran
			// toda la empresa, no tiene caso colgarles subordinados.
			reassignCandidates.value = data.filter(
				(candidate) =>
					candidate.id !== userEdit.value?.id &&
					(candidate.roles ?? []).includes('Supervisor')
			)
		} finally {
			loadingReassignCandidates.value = false
		}
	}

	const syncRoles = async (
		roleIds: number[],
		newSupervisorId?: number,
		releaseSubordinates?: boolean
	): Promise<boolean> => {
		try {
			const hadSupervisor = userEdit.value?.supervisorId

			const data = await updateRoleAsync({
				params: { id: form.id, roles: roleIds, newSupervisorId, releaseSubordinates },
			})

			if (data) {
				toast.success('Roles actualizados del usuario correctamente')
				await refetchUser()

				// Jerarquía estricta: si el supervisor anterior ya no aplica para el
				// nuevo rol, el backend lo dejó en NULL — abrimos el modal para
				// asignarle uno válido (o dejarlo sin supervisor), como en el flujo
				// de recolgado de subordinados.
				if (hadSupervisor && !userEdit.value?.supervisorId) {
					ownSupervisorSelection.value = null
					showOwnSupervisorModal.value = true
					// Re-busca candidatos con el rol NUEVO del usuario
					refetchSupervisors()
				}
			}

			return true
		} catch (error: any) {
			const reassignError =
				error?.response?.status === 422 ?
					(error?.response?.data?.errors?.newSupervisorId?.[0] ??
						error?.response?.data?.errors?.new_supervisor_id?.[0])
				:	undefined

			if (reassignError) {
				pendingRolesForReassign.value = roleIds
				reassignMessage.value = reassignError
				reassignSelection.value = null
				showReassignModal.value = true
				searchReassignCandidates()

				return false
			}

			toast.error(
				error?.response?.data?.message ||
					'Error al actualizar los roles del usuario'
			)

			return false
		}
	}

	const confirmReassignSupervisor = async () => {
		if (!reassignSelection.value) {
			toast.error('Elige el nuevo supervisor para los subordinados')
			return
		}

		const ok = await syncRoles(
			pendingRolesForReassign.value,
			reassignSelection.value.id
		)

		if (ok) {
			showReassignModal.value = false
			pendingRolesForReassign.value = []
			reassignSelection.value = null
			await refetchUser()
		}
	}

	// "Dejar libres": los subordinados quedan sin supervisor (supervisor_id NULL);
	// TM y Master los siguen viendo/administrando por alcance de empresa.
	const confirmReleaseSubordinates = async () => {
		const ok = await syncRoles(pendingRolesForReassign.value, undefined, true)

		if (ok) {
			showReassignModal.value = false
			pendingRolesForReassign.value = []
			reassignSelection.value = null
			await refetchUser()
		}
	}

	const cancelReassignSupervisor = async () => {
		showReassignModal.value = false
		pendingRolesForReassign.value = []
		reassignSelection.value = null
		// El cambio de rol NO se aplicó en el server: re-sincroniza el form.
		await refetchUser()
	}

	const addPendingRolesUpdate = async () => {
		// Un solo rol: el pendiente (si hay) reemplaza al actual, no se concatena.
		const roles =
			rolesPendingAssignment.length ?
				rolesPendingAssignment.map((role) => role.id)
			:	form.roles?.map((role) => role.id) || []

		await syncRoles(roles)
	}

	const removeRoleForm = async (roleId: number) => {
		form.roles = form.roles?.filter((r) => r.id !== roleId)

		await syncRoles(form.roles?.map((role) => role.id) || [])
	}

	/**
	 * Supervisores ADICIONALES. Aprueban y reciben notificaciones del viajero, pero
	 * NO son su padre jerárquico: no lo ven en sus listados. El principal sigue
	 * siendo `supervisor_id` y es el único que define la jerarquía.
	 */
	const additionalSupervisors = ref<User[]>([])

	const additionalSupervisorIds = computed(() =>
		additionalSupervisors.value.map((supervisor) => supervisor.id)
	)

	/** Ids guardados, para saber si de verdad hubo cambio antes de mandar nada. */
	const savedAdditionalSupervisorIds = computed(
		() => userEdit.value?.additionalSupervisorIds ?? []
	)

	const additionalSupervisorsChanged = computed(() => {
		const saved = [...savedAdditionalSupervisorIds.value].sort()
		const next = [...additionalSupervisorIds.value].sort()

		return JSON.stringify(saved) !== JSON.stringify(next)
	})

	/**
	 * Candidatos: los mismos que para el principal, menos el propio usuario y menos
	 * el principal (ese ya aprueba por `supervisor_id`; repetirlo no agrega nada y
	 * el backend lo descarta de todos modos).
	 */
	const additionalSupervisorOptions = computed(() =>
		(filteredSupervisors.value ?? []).filter(
			(candidate: User) =>
				candidate.id !== userEdit.value?.id &&
				candidate.id !== (form.supervisor?.id ?? userEdit.value?.supervisorId)
		)
	)

	const removeAdditionalSupervisor = (id: number) => {
		additionalSupervisors.value = additionalSupervisors.value.filter(
			(supervisor) => supervisor.id !== id
		)
	}

	// Rehidratar cuando llega (o se refresca) el usuario, para no dejar la lista
	// desfasada tras guardar.
	watch(
		() => userEdit.value?.additionalSupervisors,
		(supervisors) => {
			additionalSupervisors.value = (supervisors ?? []).map(
				(supervisor) => supervisor as unknown as User
			)
		},
		{ immediate: true }
	)

	const showSupervisorModal = ref(false)

	// Estados para la nueva UI de supervisor
	const showSupervisorCombobox = ref(false)
	const currentSupervisor = ref<User | undefined>(undefined)
	// Snapshot para poder deshacer "Quitar" con Cancelar (ver handleRemoveSupervisor).
	const previousSupervisor = ref<User | undefined>(undefined)

	const onSubmit = async () => {
		try {
			// Actualizar roles si hay un pendiente sin aplicar (el botón "Aplicar"
			// ya cubre el caso normal; esto es la red de seguridad si el usuario
			// eligió un rol y le dio Guardar directo sin pasar por Aplicar).
			if (
				hasFormChangesRoles.value &&
				canManageTargetRoles.value &&
				hasPermissionUser('update-users-roles', 'App\\Models\\Company')
			) {
				// Un solo rol: el pendiente (si hay) reemplaza al actual.
				const roleIds =
					rolesPendingAssignment.length ?
						rolesPendingAssignment.map((role) => role.id)
					:	form.roles?.map((role) => role.id) || []

				await syncRoles(roleIds)
			}

			// pendingSupervisorSelection se limpia tras confirmar el modal de
			// cambio (ver confirmSupervisorChange); form.supervisor es la
			// fuente de verdad tras eso.
			const nextSupervisorId =
				pendingSupervisorSelection.value?.id ?? form.supervisor?.id ?? null

			if (nextSupervisorId === userEdit.value?.id) {
				toast.error('Un usuario no puede ser su propio supervisor')
				return
			}

			// form.supervisor se hidrata async (fetch aparte del supervisor) —
			// mientras no resuelve, nextSupervisorId cae en null aunque el
			// usuario SÍ tenga supervisor. Sin este check, guardar durante esa
			// ventana (o si ese fetch falla) manda supervisorId:null y borra un
			// supervisor real que nunca se tocó. Solo se manda si de verdad cambió.
			const supervisorChanged =
				nextSupervisorId !== (userEdit.value?.supervisorId ?? null)

			if (supervisorChanged && nextSupervisorId) {
				showSupervisorCombobox.value = false
			}

			const payload: {
				id: number
				supervisorId?: number | null
				additionalSupervisorIds?: number[]
			} = {
				id: form.id,
			}
			if (supervisorChanged) {
				payload.supervisorId = nextSupervisorId
			}
			// Sólo se manda si cambió: la llave ausente le dice al backend que no
			// toque la lista (regla `sometimes`), y un arreglo vacío sí la vacía.
			if (additionalSupervisorsChanged.value) {
				payload.additionalSupervisorIds = additionalSupervisorIds.value
			}

			const data = await updateUserAsync(payload)

			if (data) {
				await refetchUser()

				if (supervisorChanged) {
					if (nextSupervisorId) {
						// Se asignó/cambió supervisor: refetch para traer sus datos.
						supervisorId.value = nextSupervisorId
						await refetchSupervisor()
					} else {
						// Se quitó el supervisor: limpiar el estado local, si no
						// queda mostrando al anterior hasta recargar la página.
						currentSupervisor.value = undefined
					}
				}

				pendingSupervisorSelection.value = null
				// updateUser (useUser.ts) ya muestra su propio toast de éxito;
				// uno aquí encima solo duplicaba el mensaje.
			}
		} catch (error: any) {
			console.log(error)

			// Muestra el error de campo del 422 (p. ej. "El supervisor debe tener
			// rol Supervisor.") en vez de tragárselo con un mensaje genérico.
			const fieldErrors = error?.response?.data?.errors
			const firstFieldError =
				fieldErrors ?
					(Object.values(fieldErrors)[0] as string[] | undefined)?.[0]
				:	undefined

			toast.error(
				firstFieldError ||
					error.response?.data?.message ||
					'Error al actualizar la información laboral'
			)
		}
	}

	const query = ref('')

	const filterRoles = computed(() =>
		query.value === '' ?
			rolesNoDuplicate.value
		:	rolesNoDuplicate.value?.filter((role: Role) => {
				return getRoleClasses(role.name.toLowerCase())
					.label?.toLowerCase()
					.includes(query.value.toLowerCase())
			})
	)

	// Computed para filtrar supervisores excluyendo al usuario actual
	// Jerarquía estricta y sin brincos (el backend valida igual): el supervisor
	// válido depende del ROL del usuario editado — Traveler → solo Supervisor;
	// Supervisor → solo Travel Manager; TM → solo Master. Copia de
	// RoleHierarchy::SUPERVISOR_ROLES_FOR (BookingV2), que a su vez tiene que decir lo
	// mismo que el panel interno: si esta tabla ofrece un jefe que el backend rechaza,
	// el usuario se come un 422 al guardar.
	const ALLOWED_SUPERVISOR_ROLES: Record<string, string[]> = {
		Traveler: ['Supervisor'],
		Supervisor: ['Travel Manager'],
		'Travel Manager': ['Master'],
		Master: [],
	}

	const allowedSupervisorRolesForUser = computed(() => {
		const roleName = userEdit.value?.roles?.[0] ?? 'Traveler'
		return ALLOWED_SUPERVISOR_ROLES[roleName] ?? ['Supervisor']
	})

	// Texto de ayuda: el buscador de supervisor solo muestra usuarios con el
	// rol exacto que aplica según la jerarquía — sin esto, "Sin resultados"
	// se sentía como que el combobox estaba roto en vez de filtrando por rol.
	const allowedSupervisorRolesLabel = computed(() =>
		allowedSupervisorRolesForUser.value
			.map((role) => getRoleClasses(role).label ?? role)
			.join(' o ')
	)

	const filteredSupervisors = computed(() => {
		if (!supervisors.value) return []

		// Excluir al usuario actual y a quien no tenga rol válido de encargado
		return supervisors.value.filter(
			(sup: User) =>
				sup.id !== userEdit.value?.id &&
				(sup.roles ?? []).some((role) =>
					allowedSupervisorRolesForUser.value.includes(role)
				)
		)
	})

	// Handlers para la funcionalidad de supervisor
	const handleSupervisorSelection = (selected: User[]) => {
		if (selected?.length > 0) {
			pendingSupervisorSelection.value = selected[0]
			showSupervisorModal.value = true
		}
	}

	const handleRemoveSupervisor = () => {
		previousSupervisor.value = currentSupervisor.value
		form.supervisor = undefined
		currentSupervisor.value = undefined
		showSupervisorCombobox.value = true
		pendingSupervisorSelection.value = null
	}

	const cancelSupervisorChange = () => {
		showSupervisorCombobox.value = false
		pendingSupervisorSelection.value = null
		// Deshace un "Quitar" pendiente (aún no guardado) restaurando el snapshot.
		if (previousSupervisor.value) {
			form.supervisor = previousSupervisor.value
			currentSupervisor.value = previousSupervisor.value
			previousSupervisor.value = undefined
		}
	}

	const confirmSupervisorChange = async () => {
		if (pendingSupervisorSelection.value) {
			form.supervisor = pendingSupervisorSelection.value
			currentSupervisor.value = pendingSupervisorSelection.value
			showSupervisorCombobox.value = false

			// Actualizar el supervisorId y hacer refetch para obtener los datos completos
			supervisorId.value = pendingSupervisorSelection.value.id
			await refetchSupervisor()
		}
		showSupervisorModal.value = false
		pendingSupervisorSelection.value = null
	}

	const cancelModalSupervisorChange = () => {
		showSupervisorModal.value = false
		pendingSupervisorSelection.value = null
	}

	// ── Nuevo supervisor propio tras un cambio de rol ─────────────────────────
	// Cuando el cambio de rol deja al usuario sin supervisor (el anterior ya no
	// aplica por jerarquía estricta), este modal ofrece asignarle uno válido
	// (candidatos ya filtrados por el rol nuevo) o dejarlo sin supervisor —
	// mismo patrón que el recolgado de subordinados, sin sensación de fallo.
	const showOwnSupervisorModal = ref(false)
	const ownSupervisorSelection = ref<User | null>(null)

	const leaveWithoutSupervisor = () => {
		showOwnSupervisorModal.value = false
		ownSupervisorSelection.value = null
	}

	const assignOwnSupervisor = async () => {
		if (!ownSupervisorSelection.value) {
			toast.error('Elige un supervisor o deja al usuario sin supervisor')
			return
		}

		try {
			await updateUserAsync({
				id: props.user.id,
				supervisorId: ownSupervisorSelection.value.id,
			})

			toast.success('Supervisor asignado correctamente')
			showOwnSupervisorModal.value = false
			ownSupervisorSelection.value = null
			await refetchUser()
		} catch (error: any) {
			const fieldErrors = error?.response?.data?.errors
			const firstFieldError =
				fieldErrors ?
					(Object.values(fieldErrors)[0] as string[] | undefined)?.[0]
				:	undefined

			toast.error(
				firstFieldError ||
					error?.response?.data?.message ||
					'Error al asignar el supervisor'
			)
		}
	}

	// Watch para cargar supervisores cuando se carga el usuario
	watch(
		() => userEdit.value?.companyId,
		(newCompanyId) => {
			if (newCompanyId) {
				// Hacer refetch de supervisores cuando cambie la compañía
				refetchSupervisors()
			}
		},
		{ immediate: true }
	)

	// Watch para sincronizar el supervisor del backend con el estado local
	watch(
		supervisor,
		(newSupervisor) => {
			currentSupervisor.value = newSupervisor
		},
		{ immediate: true }
	)

	// Watch para resetear el supervisor cuando cambia de usuario
	watch(
		() => userEdit.value?.id,
		(newUserId, oldUserId) => {
			if (newUserId !== oldUserId) {
				// Resetear el supervisor cuando cambia de usuario
				currentSupervisor.value = undefined
				showSupervisorCombobox.value = false
			}
		}
	)

	const masterHasNotAddSupervisor = computed(() => {
		if (!userEdit.value?.roles || userEdit.value.roles.length === 0) {
			return false
		}

		const hasMasterRole = userEdit.value.roles.some((role) => role === 'Master')

		return !hasMasterRole
	})

	// Un solo loading para toda la sección: el supervisor se trae en un fetch
	// aparte (arranca hasta saber el supervisorId del usuario), así que sin
	// esto el skeleton grande desaparecía y luego brincaba un mini-skeleton
	// para la tarjeta de supervisor — se sentía como doble carga.
	const isSectionLoading = computed(
		() =>
			isLoadingUser.value ||
			isFetchingUser.value ||
			(!!userEdit.value?.supervisorId &&
				(isLoadingSupervisor.value || isFetchingSupervisor.value))
	)
</script>

<template>
	<div
		v-if="!isTravelerOnly"
		class="space-y-6"
	>
		<RoundedBox class="bg-white">
			<div class="mb-6">
				<h3 class="text-lg font-medium text-gray-800 mb-1">
					Información Laboral
				</h3>
				<p class="text-sm text-gray-600">Supervisor y roles asignados</p>
			</div>

			<div v-if="isSectionLoading">
				<SkeletonLoader variant="complex-card" />
			</div>

			<div
				v-else
				class="space-y-6"
			>
				<!-- Supervisor Section -->
				<div
					class="relative"
					v-if="masterHasNotAddSupervisor"
				>
					<label class="block text-sm font-medium text-gray-700 mb-2">
						Supervisor Asignado
					</label>

					<!-- Mostrar card del supervisor cuando existe y no se está editando -->
					<div
						v-if="currentSupervisor && !showSupervisorCombobox"
						class="mb-3"
					>
						<div
							class="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
						>
							<div
								class="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-medium"
							>
								{{ currentSupervisor?.name?.charAt(0)
								}}{{ currentSupervisor?.lastname?.charAt(0) }}
							</div>
							<div class="flex-1">
								<p class="text-sm font-medium text-gray-900">
									{{ currentSupervisor?.name }}
									{{ currentSupervisor?.lastname }}
								</p>
								<p class="text-xs text-gray-500">
									{{ currentSupervisor?.email }}
								</p>
							</div>
							<div class="flex gap-2">
								<button
									type="button"
									class="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
									@click="showSupervisorCombobox = true"
								>
									Cambiar
								</button>
								<button
									type="button"
									class="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
									@click="handleRemoveSupervisor"
								>
									Quitar
								</button>
							</div>
						</div>
					</div>

					<!-- Combobox para buscar y seleccionar supervisor (visible cuando no hay supervisor o se está editando) -->
					<div v-else>
						<!-- Mensaje cuando no tiene supervisor -->

						<div
							v-if="!currentSupervisor"
							class="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
						>
							<p class="text-sm text-yellow-800">
								<svg
									class="inline w-4 h-4 mr-1"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fill-rule="evenodd"
										d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
										clip-rule="evenodd"
									></path>
								</svg>
								Este usuario no tiene un supervisor asignado
							</p>
						</div>

						<StyledCombobox
							:options="filteredSupervisors || []"
							v-model="pendingSupervisorSelection"
							:is-required="false"
							placeholder="Buscar supervisor..."
							class="transition-all duration-200"
							:prevent-auto-focus="true"
							:prevent-scroll-on-focus="true"
							@input-change="(e) => { setSupervisorQuery(e); debouncedSearchSupervisors() }"
							@update:model-value="handleSupervisorSelection"
							:show-input="!pendingSupervisorSelection"
						>
							<template #default="{ option }">
								<div class="flex items-center gap-3 py-2">
									<div
										class="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-medium"
									>
										{{ option.name?.charAt(0)
										}}{{ option.lastname?.charAt(0) }}
									</div>
									<div class="flex-1">
										<span class="text-sm font-medium">
											{{ option.name }} {{ option.lastname }}
										</span>
										<p class="text-xs text-gray-500">
											{{ option.email }} · {{ option.subordinatesCount ?? 0 }}
											subordinados
										</p>
									</div>
									<div
										v-if="pendingSupervisorSelection?.id === option.id"
										class="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center"
									>
										<svg
											class="w-3 h-3 text-white"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
									</div>
								</div>
							</template>

							<template #input-overlap>
								<div>
									<p>
										{{ pendingSupervisorSelection?.fullName }}
										<button
											v-if="pendingSupervisorSelection"
											type="button"
											class="ml-2 text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
											@click="pendingSupervisorSelection = null"
										>
											X
										</button>
									</p>
								</div>
							</template>
						</StyledCombobox>

						<!-- Botones de acción cuando hay un supervisor temporal seleccionado -->
						<div
							v-if="showSupervisorCombobox"
							class="flex gap-2 mt-2"
						>
							<button
								type="button"
								class="text-xs px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
								@click="cancelSupervisorChange"
							>
								Cancelar
							</button>
						</div>

						<!-- Mensaje de ayuda -->
						<div class="mt-2">
							<p class="text-xs text-gray-500">
								💡 Solo aparecen usuarios con rol
								<strong>{{ allowedSupervisorRolesLabel }}</strong>
								— si no ves resultados, puede que no haya ninguno con ese rol
								en la empresa.
							</p>

							<p class="text-xs text-gray-800 mt-1">
								Al asignar un supervisor, ya no podrás editar los datos
								personales de este usuario. Solo los roles Administrador y
								Gerente de viajes podrán seguir editándolo.
							</p>
						</div>

						<!-- Supervisores adicionales -->
						<div class="mt-6 pt-5 border-t border-gray-100">
							<h3 class="text-sm font-medium text-gray-900">
								Supervisores adicionales
							</h3>
							<p class="text-xs text-gray-500 mt-0.5 mb-3">
								Además del supervisor asignado, estas personas pueden aprobar
								sus solicitudes y reciben sus notificaciones. No lo verán en sus
								listados de usuarios: eso sigue dependiendo del supervisor
								asignado.
							</p>

							<div
								v-if="additionalSupervisors.length"
								class="flex flex-wrap gap-2 mb-3"
							>
								<span
									v-for="supervisor in additionalSupervisors"
									:key="supervisor.id"
									class="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-800 pl-3 pr-1.5 py-1 text-xs font-medium"
								>
									{{ supervisor.fullName || supervisor.name }}
									<button
										type="button"
										class="rounded-full w-4 h-4 flex items-center justify-center hover:bg-blue-200 transition-colors"
										:aria-label="`Quitar a ${supervisor.fullName || supervisor.name}`"
										@click="removeAdditionalSupervisor(supervisor.id)"
									>
										×
									</button>
								</span>
							</div>
							<p
								v-else
								class="text-xs text-gray-400 italic mb-3"
							>
								Sin supervisores adicionales.
							</p>

							<StyledCombobox
								multiple
								:options="additionalSupervisorOptions"
								v-model="additionalSupervisors"
								:display-value="(option: User) => option?.fullName ?? ''"
								:loading-options="isFetchingSupervisors"
								placeholder="Agregar supervisores adicionales..."
								@input-change="
									(e: string) => {
										setSupervisorQuery(e)
										debouncedSearchSupervisors()
									}
								"
							>
								<template #default="{ option }">
									<div class="flex flex-col py-1">
										<span class="text-sm font-medium">{{
											option.fullName
										}}</span>
										<span class="text-xs text-gray-500">{{
											option.email
										}}</span>
									</div>
								</template>
							</StyledCombobox>
						</div>
					</div>
				</div>

				<!-- Roles Section -->
				<div class="relative">
					<!-- Show skeleton while loading -->
					<div v-if="isLoadingRoles">
						<RolesSkeleton :roles-count="3" />
					</div>

					<!-- Show actual content when loaded -->
					<div v-else>
						<div
							v-if="
								hasPermissionUser(
									'update-users-roles',
									'App\\Models\\Company'
								)
							"
						>
							<label class="block text-sm font-medium text-gray-700 mb-2">
								Roles Asignados
							</label>

							<!-- Selected Roles Display -->
							<div
								v-if="form.roles && form.roles.length > 0"
								class="mb-3"
							>
								<div class="flex flex-wrap gap-2">
									<span
										v-for="role in form.roles"
										:key="role.id"
										class="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full transition-all duration-200"
										:class="getRoleClasses(role.name).class"
									>
										{{ getRoleClasses(role.name).label }}
										<button
											v-if="form.roles.length > 1 && canManageTargetRoles"
											type="button"
											class="w-4 h-4 rounded-full transition-colors duration-200 flex items-center justify-center text-xs"
											@click="
												() => {
													form.roles = form.roles?.filter(
														(r) => r.id !== role.id
													)

													removeRoleForm(role.id)
												}
											"
										>
											×
										</button>
									</span>
								</div>
							</div>

							<!-- Solo lectura: el actor ve el rol pero no puede cambiarlo -->
							<p
								v-if="!canManageTargetRoles"
								class="text-xs text-gray-500"
							>
								Este usuario tiene un rol que no puedes modificar. Puedes editar
								el resto de su información.
							</p>

							<!-- Preview de roles pendientes de asignar. Lleva la misma guarda
							     que el combobox: hoy sin él no hay forma de poblar esta lista,
							     pero así queda cerrada si mañana se puebla desde otro lado. -->
							<template
								v-if="canManageTargetRoles && rolesPendingAssignment.length > 0"
							>
								<div
									class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
								>
									<div class="flex items-center justify-between mb-2">
										<span class="text-sm font-medium text-blue-800">
											Roles a asignar ({{ rolesPendingAssignment.length }})
										</span>
										<div class="flex gap-2">
											<button
												type="button"
												@click="
													() => {
														applyPendingRoles()
														addPendingRolesUpdate()
													}
												"
												class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
											>
												Aplicar
											</button>
											<button
												type="button"
												@click="clearPendingRoles"
												class="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
											>
												Limpiar
											</button>
										</div>
									</div>
									<div class="flex flex-wrap gap-2">
										<span
											v-for="role in rolesPendingAssignment"
											:key="role.id"
											class="inline-flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border-2 border-dashed border-blue-300"
										>
											{{ getRoleClasses(role.name).label }}
											<button
												type="button"
												class="w-3 h-3 rounded-full hover:bg-blue-200 transition-colors duration-200 flex items-center justify-center text-xs"
												@click="removeRoleFromPending(role.id)"
											>
												×
											</button>
										</span>
									</div>
								</div>
							</template>

							<StyledCombobox
								v-if="canManageTargetRoles"
								:options="filterRoles || []"
								:model-value="[]"
								:is-required="false"
								:placeholder="
									form.roles && form.roles.length > 0 ?
										'Agregar más roles...'
									:	'Seleccionar roles...'
								"
								multiple
								class="transition-all duration-200 prevent-scroll-focus"
								:prevent-auto-focus="true"
								:prevent-scroll-on-focus="true"
								tabindex="-1"
								@input-change="(e) => (query = e)"
								@update:model-value="
									(selectedRoles: Role[]) => {
										selectedRoles.forEach((role: Role) => {
											addRoleToPending(role)
										})
									}
								"
							>
								<template #default="{ option }">
									<div class="flex items-center gap-3 py-2">
										<div
											class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium"
										>
											{{ getRoleClasses(option.name)?.label?.charAt(0) }}
										</div>
										<div class="flex-1 min-w-0">
											<span class="text-sm font-medium">
												{{ getRoleClasses(option.name).label }}
											</span>
											<!-- Sin esto el nombre del rol no dice qué habilita, y
											     quien asigna permisos tiene que adivinar. -->
											<p
												v-if="getRoleClasses(option.name).description"
												class="text-xs text-gray-500 mt-0.5"
											>
												{{ getRoleClasses(option.name).description }}
											</p>
										</div>
										<div
											v-if="form.roles?.some((r) => r.id === option.id)"
											class="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center"
										>
											<svg
												class="w-3 h-3 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M5 13l4 4L19 7"
												></path>
											</svg>
										</div>
									</div>
								</template>
							</StyledCombobox>

							<div
								v-if="!form.roles || form.roles.length === 0"
								class="mt-2"
							>
								<p class="text-sm text-gray-500">No hay roles asignados.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</RoundedBox>

		<template v-if="isSectionLoading">
			<SkeletonLoader
				variant="card"
				:count="1"
			/>
		</template>

		<template v-else>
			<RoundedBox class="bg-white">
				<div class="flex justify-end gap-3">
					<StyledButton
						v-if="
							canEditProfile(userLogged?.id ?? 0, props.user.id) ||
							hasPermissionUser('update-users', 'App\\Models\\Company')
						"
						:label="hasFormChanges ? 'Guardar Cambios' : 'No hay cambios'"
						class="bg-gradient-to-r text-white transition-all duration-200 transform hover:scale-105 shadow-lg"
						:class="
							hasFormChanges ?
								'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
							:	'from-slate-400 to-gray-600 cursor-not-allowed opacity-50 hover:from-slate-600 hover:to-gray-600'
						"
						type="button"
						:disabled="!hasFormChanges"
						@click="onSubmit"
					/>
				</div>
			</RoundedBox>
		</template>
	</div>

	<ModalComponent
		:model-value="showSupervisorModal"
		@update:model-value="(show) => (showSupervisorModal = show)"
	>
		<template #modalBody>
			<div class="p-6">
				<div class="flex items-center mb-4">
					<div class="bg-blue-100 rounded-full p-2">
						<svg
							class="w-6 h-6 text-blue-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							></path>
						</svg>
					</div>
					<h2 class="ml-3 text-lg font-semibold text-gray-800">
						Confirmar Cambio de Supervisor
					</h2>
				</div>
				<div class="mb-6">
					<p class="text-sm text-gray-700">
						¿Estás seguro de que deseas asignar a
						<strong class="text-gray-900">{{
							pendingSupervisorSelection?.name
						}}</strong>
						como supervisor?
					</p>
				</div>

				<div class="flex justify-end gap-3">
					<StyledButton
						label="Cancelar"
						class="bg-gray-200 text-gray-700 hover:bg-gray-300"
						type="button"
						@click="cancelModalSupervisorChange"
					/>
					<StyledButton
						label="Confirmar"
						class="bg-blue-600 text-white hover:bg-blue-700"
						type="button"
						@click="confirmSupervisorChange"
					/>
				</div>
			</div>
		</template>
	</ModalComponent>

	<!-- Recolgado de subordinados: obligatorio al bajar a Traveler a un usuario con gente a su cargo (el backend exige newSupervisorId o dejarlos libres) -->
	<ModalComponent
		:model-value="showReassignModal"
		@update:model-value="(show: boolean) => !show && cancelReassignSupervisor()"
	>
		<template #modalBody>
			<div class="p-6">
				<div class="flex items-center mb-4">
					<div class="bg-amber-100 rounded-full p-2">
						<svg
							class="w-6 h-6 text-amber-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 9v2m0 4h.01M5.062 19h13.876c1.54 0 2.502-1.667 1.732-3L13.732 5c-.77-1.333-2.694-1.333-3.464 0L4.33 16c-.77 1.333.192 3 1.732 3z"
							></path>
						</svg>
					</div>
					<h2 class="ml-3 text-lg font-semibold text-gray-800">
						Reasignar subordinados
					</h2>
				</div>

				<p class="text-sm text-gray-700 mb-2">{{ reassignMessage }}</p>
				<p class="text-xs text-gray-500 mb-4">
					Reasígnalos a un usuario con rol <strong>Supervisor</strong>, o déjalos
					libres (sin supervisor) — los Travel Manager y Master de la empresa los
					siguen viendo y administrando de todas formas.
				</p>

				<StyledCombobox
					:options="reassignCandidates"
					v-model="reassignSelection"
					:is-required="false"
					:loading-options="loadingReassignCandidates"
					placeholder="Buscar supervisor (rol Supervisor)..."
					:prevent-auto-focus="true"
					:show-input="!reassignSelection"
					@input-change="(e) => searchReassignCandidates(e)"
					@update:model-value="
						(sel) =>
							(reassignSelection = Array.isArray(sel) ? (sel[0] ?? null) : sel)
					"
				>
					<template #default="{ option }">
						<div class="flex flex-col py-1">
							<span class="text-sm font-medium">
								{{ option.name }} {{ option.lastname }}
							</span>
							<span class="text-xs text-gray-500">
								{{ option.email }} · {{ option.subordinatesCount ?? 0 }} subordinados
							</span>
						</div>
					</template>

					<template #input-overlap>
						<div>
							<p>
								{{ reassignSelection?.fullName }}
								<button
									v-if="reassignSelection"
									type="button"
									class="ml-2 text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
									@click="reassignSelection = null"
								>
									X
								</button>
							</p>
						</div>
					</template>
				</StyledCombobox>

				<div class="flex justify-end gap-3 mt-6">
					<StyledButton
						label="Cancelar"
						class="bg-gray-200 text-gray-700 hover:bg-gray-300"
						type="button"
						@click="cancelReassignSupervisor"
					/>
					<StyledButton
						label="Dejar libres"
						class="bg-amber-500 text-white hover:bg-amber-600"
						type="button"
						v-tooltip="'Los subordinados quedan sin supervisor asignado'"
						@click="confirmReleaseSubordinates"
					/>
					<StyledButton
						label="Reasignar y aplicar"
						:class="[
							'text-white',
							reassignSelection ?
								'bg-blue-600 hover:bg-blue-700'
							:	'bg-blue-300 cursor-not-allowed',
						]"
						type="button"
						:disabled="!reassignSelection"
						@click="confirmReassignSupervisor"
					/>
				</div>
			</div>
		</template>
	</ModalComponent>

	<!-- Nuevo supervisor tras cambio de rol: el anterior ya no aplica por la jerarquía estricta y el usuario quedó sin supervisor -->
	<ModalComponent
		:model-value="showOwnSupervisorModal"
		@update:model-value="(show: boolean) => !show && leaveWithoutSupervisor()"
	>
		<template #modalBody>
			<div class="p-6">
				<div class="flex items-center mb-4">
					<div class="bg-amber-100 rounded-full p-2">
						<svg
							class="w-6 h-6 text-amber-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
							></path>
						</svg>
					</div>
					<h2 class="ml-3 text-lg font-semibold text-gray-800">
						Asignar nuevo supervisor
					</h2>
				</div>

				<p class="text-sm text-gray-700 mb-4">
					Con el cambio de rol, el supervisor anterior ya no aplica y el usuario
					quedó <strong>sin supervisor</strong>. Puedes asignarle uno válido para
					su nuevo rol, o dejarlo sin supervisor.
				</p>
				<p class="text-xs text-gray-500 mb-4">
					💡 Solo aparecen usuarios con rol
					<strong>{{ allowedSupervisorRolesLabel }}</strong>
					— si no ves resultados, puede que no haya ninguno con ese rol en
					la empresa.
				</p>

				<StyledCombobox
					:options="filteredSupervisors || []"
					v-model="ownSupervisorSelection"
					:is-required="false"
					:loading-options="isFetchingSupervisors"
					placeholder="Buscar supervisor válido..."
					:prevent-auto-focus="true"
					:show-input="!ownSupervisorSelection"
					@input-change="(e) => { setSupervisorQuery(e); debouncedSearchSupervisors() }"
					@update:model-value="
						(sel) =>
							(ownSupervisorSelection =
								Array.isArray(sel) ? (sel[0] ?? null) : sel)
					"
				>
					<template #default="{ option }">
						<div class="flex flex-col py-1">
							<span class="text-sm font-medium">
								{{ option.name }} {{ option.lastname }}
							</span>
							<span class="text-xs text-gray-500">
								{{ option.email }} · {{ option.subordinatesCount ?? 0 }} subordinados
							</span>
						</div>
					</template>

					<template #input-overlap>
						<div>
							<p>
								{{ ownSupervisorSelection?.fullName }}
								<button
									v-if="ownSupervisorSelection"
									type="button"
									class="ml-2 text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
									@click="ownSupervisorSelection = null"
								>
									X
								</button>
							</p>
						</div>
					</template>
				</StyledCombobox>

				<div class="flex justify-end gap-3 mt-6">
					<StyledButton
						label="Dejar sin supervisor"
						class="bg-gray-200 text-gray-700 hover:bg-gray-300"
						type="button"
						@click="leaveWithoutSupervisor"
					/>
					<StyledButton
						label="Asignar supervisor"
						:class="[
							'text-white',
							ownSupervisorSelection ?
								'bg-blue-600 hover:bg-blue-700'
							:	'bg-blue-300 cursor-not-allowed',
						]"
						type="button"
						:disabled="!ownSupervisorSelection"
						@click="assignOwnSupervisor"
					/>
				</div>
			</div>
		</template>
	</ModalComponent>
</template>
